import classNames from "classnames";
import { AriaAttributes, ReactNode } from "react";
import { FieldProps } from "types";
import { getFieldIds } from "utils/field";
import { FieldError } from "./FieldError";
import { FieldLabel } from "./FieldLabel";

const getFieldOptions = ({ id, name, error }: FieldProps) => {
  const { computedId, errorId } = getFieldIds({ id, name });

  return {
    errorId,
    computedProps: {
      id: computedId,
      name,
      className: classNames(
        "block w-full border focus:outline-none sm:text-sm rounded-md bg-white disabled:bg-gray-100 disabled:cursor-not-allowed opacity-100",
        error
          ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 disabled:bg-red-50"
          : "focus:ring-indigo-500 focus:border-indigo-500  border-gray-300"
      ),
      ...(error
        ? ({
            "aria-invalid": "true",
            "aria-describedby": errorId,
          } as Pick<AriaAttributes, "aria-invalid" | "aria-describedby">)
        : {}),
    },
  };
};

export interface FieldGroupProps extends FieldProps {
  children: (
    props: ReturnType<typeof getFieldOptions>["computedProps"]
  ) => ReactNode;
}

export const FieldGroup = ({
  label,
  error,
  optional,
  children,
  required,
  ...props
}: FieldGroupProps) => {
  const { computedProps, errorId } = getFieldOptions({
    error,
    optional,
    ...props,
  });

  return (
    <div>
      {label && (
        <FieldLabel
          htmlFor={computedProps.id}
          optional={optional}
          required={required}
        >
          {label}
        </FieldLabel>
      )}
      <div className="relative rounded-md shadow-sm">
        {children(computedProps)}
      </div>
      {error && <FieldError id={errorId}>{error.message}</FieldError>}
    </div>
  );
};
